import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

/**
 * Pulls the public services list, filters by `matchTitles` (case-insensitive substring match
 * against each title), flattens each service's FAQ, and renders as an accordion.
 * Keeps the page in sync with the admin-editable FAQs in the service detail modal.
 */
export function ServiceFaqSection({ matchTitles, heading = "Common questions", eyebrow, blurb, testid = "service-faq-section" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(-1);

  useEffect(() => {
    api
      .get("/services")
      .then((r) => {
        const matchers = matchTitles.map((t) => t.toLowerCase());
        const picked = (r.data || []).filter((s) => {
          const t = (s.title || "").toLowerCase();
          return matchers.some((m) => t.includes(m));
        });
        const flat = [];
        picked.forEach((svc) => {
          (svc.faq || []).forEach((f) => {
            if (f?.question && f?.answer) flat.push({ ...f, service: svc.title });
          });
        });
        setItems(flat);
      })
      .catch(() => setItems([]));
  }, [matchTitles.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) return null;

  return (
    <section className="mt-20" data-testid={testid}>
      <Reveal>
        <div className="flex items-center gap-3">
          <HelpCircle size={16} className="text-emerald-300" />
          <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {heading}
          </h2>
        </div>
        {(eyebrow || blurb) && (
          <p className="mt-2 max-w-xl text-sm text-zinc-500">
            {eyebrow ? <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">{eyebrow} · </span> : null}
            {blurb}
          </p>
        )}
      </Reveal>
      <ul className="mt-6 divide-y divide-white/10 rounded-2xl border border-white/10 bg-[#141419]">
        {items.map((item, i) => {
          const isOpen = open === i;
          return (
            <li key={`${item.service}-${i}`} data-testid={`${testid}-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                data-testid={`${testid}-toggle-${i}`}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-bold text-white transition-colors hover:text-emerald-200 sm:px-6"
              >
                <span className="flex-1">{item.question}</span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-emerald-300 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <p className="whitespace-pre-wrap px-5 pb-4 text-sm leading-relaxed text-zinc-400 sm:px-6">
                      {item.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
